import { useEffect } from 'react';

interface SEOOptions {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  /** JSON-LD structured data objects — pass one or more */
  schema?: object | object[];
}

const SITE_NAME = 'TGDirectory';
const BASE_URL = 'https://tgdirectory.app';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-cover.png`;

function setMeta(selector: string, attr: string, value: string) {
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, attrName, attrVal] = selector.match(/\[([^\]]+)="([^\]]+)"\]/) ?? [];
    if (attrName) el.setAttribute(attrName, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertSchema(id: string, data: object) {
  let el = document.querySelector<HTMLScriptElement>(`script[data-schema="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-schema', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removePageSchemas() {
  document.querySelectorAll('script[data-schema^="page-"]').forEach((el) => el.remove());
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  schema,
}: SEOOptions) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

    // Title
    document.title = fullTitle;

    // Primary meta
    setMeta('meta[name="description"]', 'content', description);
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);

    // Canonical
    const canonicalHref = canonical ?? `${BASE_URL}/`;
    setLink('canonical', canonicalHref);

    // Open Graph
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:type"]', 'content', ogType);
    setMeta('meta[property="og:url"]', 'content', canonicalHref);
    setMeta('meta[property="og:image"]', 'content', ogImage);

    // Twitter Card
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);

    // Schema.org – page-level (cleaned on each navigation)
    removePageSchemas();
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s, i) => upsertSchema(`page-${i}`, s));
    }

    // Restore defaults on unmount
    return () => {
      document.title = `${SITE_NAME} – Find & Join the Best Telegram Communities`;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, canonical, ogType, ogImage]);
}